import React, { useContext, useEffect, useRef, useState } from "react";
import ViewerContext from "../../context/ViewerContext";
import loaderHelper from "../loader/loaderHelper";
import { parseIGCBern } from "../../helper/igcParserBern";
import { v4 as uuid } from "uuid";
import { liftBelowTerrain } from "../../helper/terrainClamp";
import { computeContentWindow } from "../../helper/contentWindow";

const colors = [
  [255, 0, 0], // Red
  [0, 255, 0], // Green
  [0, 0, 255], // Blue
  [255, 255, 0], // Yellow
  [0, 255, 255], // Cyan
  [255, 0, 255], // Magenta
  [128, 128, 128], // Gray
  [128, 0, 0], // Maroon
  [128, 128, 0], // Olive
  [0, 255, 0], // Lime
  [128, 0, 128], // Purple
  [0, 128, 128], // Teal
  [0, 0, 128], // Navy
  [255, 165, 0], // Orange
  [255, 192, 203], // Pink
  [165, 42, 42], // Brown
  [255, 215, 0], // Gold
  [192, 192, 192], // Silver
];

const FileUpload = () => {
  const {
    loadedFileDataList,
    setLoadedFileDataList,
    setLoadedFileNameList,
    igcParsedData,
    setIgcParsedData,
    setAutoSwitchTail,
    setTrailDuration,
  } = useContext(ViewerContext);

  const fileInputRef = useRef(null);
  const [fileData, setFileData] = useState([]);
  const [urlData, setUrlData] = useState([]);
  const [disableFileUpload, setDisableFileUpload] = useState(false);

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const singleFilePath = urlParams.get("file");
    if (singleFilePath) {
      urlDataRequestListHandler([singleFilePath]);
    }

    const multiFilePath = urlParams.get("files");
    if (multiFilePath) {
      const fileList = multiFilePath.split(",");
      urlDataRequestListHandler(fileList);
    }

    if (singleFilePath || multiFilePath) {
      setDisableFileUpload(true);
      return;
    }
  }, []);

  useEffect(() => {
    if (fileData.length > 0 && fileData.length == loadedFileDataList.length) {
      callUploadFunctions(fileData);
      setFileData([]);
      setLoadedFileDataList([]);
    }
  }, [fileData]);

  useEffect(() => {
    if (urlData.length > 0) {
      loaderHelper().open();
      callUploadFunctions(urlData);
      setUrlData([]);
    }
  }, [urlData]);

  useEffect(() => {
    if (loadedFileDataList.length > 0) {
      loaderHelper().open();
      fileHandler();
    }
  }, [loadedFileDataList]);

  const urlDataRequestHandler = (urlAddress) => {
    fetch(urlAddress)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok " + response.statusText);
        }
        return response.text();
      })
      .then((data) => {
        setUrlData((prevData) => [...prevData, { name: "", content: data }]);
      })
      .catch((error) => {
        console.error("There was a problem with the fetch operation:", error);
      });
  };

  const urlDataRequestListHandler = (urlAddresses) => {
    if (!Array.isArray(urlAddresses)) {
      console.error("Input needs to be an array of URLs");
      return;
    }

    const igcUrls = urlAddresses.filter((url) => url.toLowerCase().endsWith(".igc")); // accepting .igc and .IGC

    Promise.all(
      igcUrls.map(async (url) => {
        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(
              "Network response was not ok " + response.statusText
            );
          }
          const data = await response.text();
          return { name: "", content: data };
        } catch (error) {
          console.error("There was a problem with the fetch operation:", error);
          return undefined;
        }
      })
    ).then((results) => {
      const successfulResults = results.filter(
        (result) => result !== undefined
      );
      setUrlData((prevData) => [...prevData, ...successfulResults]);
    });
  };

  const fileHandler = () => {
    loadedFileDataList.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileContent = e.target.result;

        setFileData((prevData) => [
          ...prevData,
          { name: file.name, content: fileContent },
        ]);

        // console.log(`File content of ${file.name}:`, e.target.result);
        // Perform any additional processing here
      };
      reader.readAsText(file);
    });
  };

  const callUploadFunctions = async (dataList) => {
    let parseDataList = [];
    let tmpData = [];
    // let availableColors = { ...colors };

    dataList.map((file, index) => {
      tmpData.push(file.content);
      // let color = getRandomUniqueColor(availableColors)
      let color = colors[index % colors.length];

      const parsedData = parseIGCBern(file.content);
      parsedData.id = uuid();
      parsedData.color = color;
      // When this track is actually worth looking at — the chart and the clock
      // span the union of these, not the hours a logger sat on a table
      // (helper/contentWindow.js). A few ms even for 86k fixes.
      parsedData.contentWindow = computeContentWindow(parsedData.data);
      parseDataList.push(parsedData);
    });

    // Tracks recorded on the ground — the walk up, the drive to launch — sit
    // below the terrain model, because GPS altitude on the ground is wrong by
    // more than the ground is thick. Raise just those stretches onto the
    // surface before anything is drawn from them. Cheap per track, but the
    // probe budget is shared out when a whole comp is loaded at once.
    const probeBudget = Math.max(150, Math.round(1500 / parseDataList.length));
    await Promise.all(
      parseDataList.map((flight) =>
        liftBelowTerrain(window.viewer, flight.data, probeBudget)
      )
    );

    // Automatically enable Switch Tail Time if more than 5 tracks are loaded
    if (dataList.length > 5) {
      setTrailDuration(20 * 60); // Set to 20 minutes BEFORE creating data sources
      setAutoSwitchTail(true);
    } else {
      setTrailDuration(80000); // Reset to default for 5 or fewer tracks
      setAutoSwitchTail(false);
    }

    // For large scenes, add data in batches to reduce render lag
    if (dataList.length > 15) {
      console.log(`Loading ${dataList.length} tracks in batches for better performance...`);
      const batchSize = 8;
      for (let i = 0; i < parseDataList.length; i += batchSize) {
        const batch = parseDataList.slice(i, i + batchSize);
        setTimeout(() => {
          setIgcParsedData(prev => [...prev, ...batch]);
        }, i * 100); // Stagger by 100ms per batch
      }
      setTimeout(() => loaderHelper().close(), parseDataList.length * 100);
    } else {
      setIgcParsedData(parseDataList);
      loaderHelper().close();
    }
  };

  function getRandomUniqueColor(colorObj) {
    const colorKeys = Object.keys(colorObj);
    if (colorKeys.length === 0) {
      throw new Error("No more colors available");
    }

    const randomIndex = Math.floor(Math.random() * colorKeys.length);
    const selectedKey = colorKeys[randomIndex];
    const selectedColor = colorObj[selectedKey];

    // Remove the selected color from the object
    delete colorObj[selectedKey];

    return selectedColor;
  }

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setLoadedFileDataList(selectedFiles);
  };

  const handleLinkClick = () => {
    fileInputRef.current.click();
  };

  return (
    <>
      <tr>
        <td colSpan={4}>
          {(!igcParsedData || igcParsedData.length == 0) && !disableFileUpload &&
            "igc Datei(en) auf die Karte ziehen (drag&drop)"}
        </td>
      </tr>

      <tr>
        <td valign="top" colSpan="4">
          {!disableFileUpload && (
            <div className="no-select-item" id="file-upload-container">
              <a
                href="#"
                className="no-select-item"
                id="upload_link"
                onClick={handleLinkClick}
              >
                <b>IGC Dateien hochladen</b>
              </a>
              <input
                type="file"
                accept=".igc"
                multiple
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </div>
          )}
        </td>
      </tr>
    </>
  );
};

export default FileUpload;
